import { styled, alpha } from '@mui/material/styles';
import { Dialog, FormControlLabel } from '@mui/material';
import { NAV_LOCATION_BANNER_HEIGHT_VAR } from 'global/components/FlexibleLayout/constants';

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paperScrollPaper': {
    backgroundColor: theme.palette.background.default,
  },

  '@media print': {
    position: 'relative !important',
    backgroundColor: theme.palette.common.white,
    display: 'block',
    '& .MuiBackdrop-root': {
      display: 'none',
    },
    '& .MuiDialog-scrollPaper': {
      display: 'block',
    },
    '& .MuiDialog-paperScrollPaper': {
      display: 'block',
      height: 'auto !important',
      backgroundColor: theme.palette.common.white,
    },
  },
}));

export const FauxPage = styled('div')(({ theme }) => ({
  margin: '20px auto',
  maxWidth: 1000,
  width: '100%',
  padding: theme.spacing(2),
  boxShadow: theme.shadows[10],
  backgroundColor: theme.palette.background.paper,

  '@media print': {
    margin: 0,
    maxWidth: '100%',
    padding: 0,
    // Leave room so page content is not covered by the fixed print copyright.
    paddingBottom: theme.spacing(4),
    boxShadow: 'none',
  },
}));

// Fixed to the bottom of each printed page (browser-dependent; Chrome repeats
// fixed elements across pages). Hidden in the on-screen print preview dialog.
export const PrintCopyright = styled('div')(({ theme }) => ({
  display: 'none',

  '@media print': {
    display: 'block',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    textAlign: 'center',
    padding: theme.spacing(0.5, 1),
    fontSize: 10,
    lineHeight: 1.2,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.common.white,
  },
}));

export const Title = styled('div')({
  fontSize: 20,
  fontWeight: 'bold',
  marginBottom: 10,
});

export const CoverTitle = styled('div')({
  fontSize: 16,
});

export const ButtonContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 100,
  '&.fixed, &.sticky': {
    position: 'fixed !important',
  },
  '&.fixed': {
    top: theme.spacing(2),
  },
  '&.sticky:not(.fixed)': {
    backgroundColor: alpha(theme.palette.background.default, 0.95),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
  },
  '&:not(.fixed)': {
    // Rest top clears the app NavBar. When the below-lg location banner is
    // showing, NavRealmBanner publishes its height so the overlay shifts down
    // instead of covering the banner (md–lg icon-only cluster).
    [theme.breakpoints.up('xs')]: {
      top: `calc(75px + var(${NAV_LOCATION_BANNER_HEIGHT_VAR}, 0px))`,
    },
    [theme.breakpoints.down('sm')]: {
      top: `calc(65px + var(${NAV_LOCATION_BANNER_HEIGHT_VAR}, 0px))`,
    },
  },

  // Spacing applies to both labeled Buttons and icon-only IconButtons.
  '& .MuiButton-root, & .MuiIconButton-root': {
    marginLeft: theme.spacing(1),
    '&:first-child': {
      marginLeft: 0,
    },
  },
  // Lock labeled Button height only — do not size-lock IconButtons (keeps them square).
  '& .MuiButton-root': {
    // Lock the height so MUI v9's default contained-button sizing doesn't make
    // these render taller than the v5 reference (PFM-2599 QA round 1).
    height: 36,
    minHeight: 36,
    lineHeight: 1.5,
  },

  // PFM-2628 (rework): on small screens the Share/Download/Print cluster no
  // longer overlays (or sits below) the assessment tabs. Two opt-in mobile
  // variants drive that, and neither touches the print dialog `fixed`/`sticky`
  // overlays:
  //
  //  - `hideOnMobile`: the in-page Tabs cluster is HIDDEN below `lg` (the
  //    desktop top-right overlay is preserved at `lg`+). Used by the PrintView
  //    that lives in the tabs AppBar so no buttons appear by/under the tabs on
  //    mobile. `lg` matches HeaderItem's `isMobile` (Author/Scorecards hide).
  //  - `headerRow`: the dedicated mobile cluster rendered (via portal) on the
  //    same row as Location/Status, right-aligned. It flows in-document below
  //    `lg`, and is hidden at `lg`+ (desktop keeps the overlay instead).
  //
  // Declared after the base `& button` rule so these overrides win below `lg`.
  '&.hideOnMobile:not(.fixed):not(.sticky)': {
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  },

  '&.headerRow:not(.fixed):not(.sticky)': {
    // Hidden on desktop — the absolute overlay above covers `lg`+.
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
    [theme.breakpoints.down('lg')]: {
      position: 'static',
      top: 'auto',
      right: 'auto',
      zIndex: 'auto',
      display: 'flex',
      justifyContent: 'flex-end',
      flexWrap: 'nowrap',
      gap: theme.spacing(1),
      padding: 0,
      // `gap` already spaces the buttons; drop the per-button left margin so
      // the cluster sits flush against the right edge of its slot.
      '& .MuiButton-root, & .MuiIconButton-root': {
        marginLeft: 0,
      },
    },
  },

  // Document-list print cluster inside FlexibleLayout: in-flow in the list
  // header so the buttons stay in the left panel (not a viewport overlay
  // trapped by the mobile slider's containing block).
  '&.inFlow:not(.fixed)': {
    position: 'static',
    top: 'auto',
    right: 'auto',
    zIndex: 'auto',
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '& .MuiButton-root, & .MuiIconButton-root': {
      marginLeft: 0,
    },
  },

  '@media print': {
    display: 'none',
  },
}));

export const PageBreak = styled('div')({
  position: 'relative',
  width: '100%',
  display: 'block',
  clear: 'both',
  // Do not use page-break-inside:avoid — tall docs were pushed to the next
  // sheet and left a blank page before content.
  '&:not(.first)': { pageBreakBefore: 'always' },

  '@media print': {
    marginBottom: 0,
  },
});

export const SectionToggles = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 4,
  padding: '4px 8px',
  marginTop: theme.spacing(1),
  boxShadow: theme.shadows[2],

  '@media print': {
    display: 'none',
  },
}));

export const SectionToggleLabel = styled(FormControlLabel)({
  '& .MuiFormControlLabel-label': {
    fontSize: 13,
  },
  '& .MuiCheckbox-root': {
    padding: '2px 4px',
  },
});
